/**
 * Browser wallet layer — bundled by esbuild into public/dist/wallet.js.
 *
 * wagmi core + viem, configured from the SAME chain.ts the server uses
 * (window.__RH_CONFIG__.chain carries the resolved values, so the browser
 * never guesses an RPC). Connectors: injected (MetaMask, Rabby, …) always;
 * WalletConnect when WALLETCONNECT_PROJECT_ID is set.
 *
 * Exposes a tiny API on window.RHWallet used by public/js/app.js:
 *   connect(kind) → address, disconnect(), address(), signMessage(msg),
 *   deposit(weiString) → txHash, waitForTx(hash), subscribe(cb), chainMismatch()
 */
import {
  createConfig, http, connect, disconnect, getAccount, signMessage, watchAccount,
  sendTransaction, waitForTransactionReceipt, switchChain, getChainId, reconnect,
} from '@wagmi/core';
import { injected, walletConnect } from '@wagmi/connectors';
import { parseAbi, encodeFunctionData, type Address, type Hex } from 'viem';
import { toViemChain, type ChainConfig } from '../../src/config/chain.ts';

declare global {
  interface Window {
    __RH_CONFIG__: { chain: ChainConfig & { depositContract: string | null; houseAddress: string | null; weiPerCent: string }; walletConnectProjectId: string | null };
    RHWallet: ReturnType<typeof createWalletApi>;
  }
}

const DEPOSIT_ABI = parseAbi(['function deposit() payable', 'function depositFor(address user) payable']);

function createWalletApi() {
  const cfgIn = window.__RH_CONFIG__.chain;
  const chain = toViemChain(cfgIn) as any;
  const connectors: any[] = [injected({ shimDisconnect: true })];
  const wcId = window.__RH_CONFIG__.walletConnectProjectId;
  if (wcId) {
    connectors.push(walletConnect({ projectId: wcId, showQrModal: true, metadata: { name: 'Loxley', description: 'Provably fair casino on Robinhood Chain', url: location.origin, icons: [] } }));
  }
  const config = createConfig({ chains: [chain], connectors, transports: { [chain.id]: http(cfgIn.rpcUrl) } });
  const listeners = new Set<(a: { address: string | null; connected: boolean }) => void>();
  watchAccount(config, {
    onChange(acc) {
      for (const cb of listeners) cb({ address: acc.address ?? null, connected: acc.status === 'connected' });
    },
  });
  void reconnect(config);

  return {
    chain: cfgIn,
    hasInjected: () => typeof (window as any).ethereum !== 'undefined',
    hasWalletConnect: () => !!wcId,
    address: () => getAccount(config).address ?? null,
    isConnected: () => getAccount(config).status === 'connected',
    async connect(kind: 'injected' | 'walletconnect' = 'injected') {
      const c = kind === 'walletconnect' ? connectors[1] : connectors[0];
      if (!c) throw new Error('connector unavailable');
      const r = await connect(config, { connector: c, chainId: chain.id });
      return r.accounts[0] as string;
    },
    async disconnect() { await disconnect(config); },
    async ensureChain() {
      const id = getChainId(config);
      if (id !== chain.id) await switchChain(config, { chainId: chain.id });
    },
    async chainMismatch() { try { return getChainId(config) !== chain.id; } catch { return false; } },
    async signMessage(message: string) { return signMessage(config, { message }); },
    async deposit(weiString: string) {
      const to = cfgIn.depositContract as Address | null;
      if (!to) throw new Error('deposit contract not configured');
      await this.ensureChain();
      const data = encodeFunctionData({ abi: DEPOSIT_ABI, functionName: 'deposit' }) as Hex;
      return sendTransaction(config, { to, value: BigInt(weiString), data, chainId: chain.id });
    },
    async waitForTx(hash: Hex) { return waitForTransactionReceipt(config, { hash, chainId: chain.id }); },
    subscribe(cb: (a: { address: string | null; connected: boolean }) => void) { listeners.add(cb); return () => listeners.delete(cb); },
  };
}

window.RHWallet = createWalletApi();
window.dispatchEvent(new CustomEvent('rh-wallet-ready'));
export {};
